import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AlertController, Platform } from "ionic-angular";
import { CanvasService } from "../../../canvas/canvas-service.interface";
import { canvas, NewWoundData, PreviousWound } from "../new-wound-site-service";
import { NewWoundService } from "../new-wound.service";

@Component({
  selector: "new-wound-canvas",
  templateUrl: "new-wound-canvas.html",
})
export class NewWoundCanvasComponent implements AfterViewInit {
  baseHeight = 100;
  baseWidth = 100;

  @ViewChild("canvas") canvasElement: ElementRef;
  @Input() canvasService: CanvasService;
  @Input() height = this.baseHeight;
  @Input() width = this.baseWidth;
  @Input() pointsMovable: boolean = true;
  @Output() onDataUpdate: EventEmitter<any> = new EventEmitter();
  private newWoundCanvas: canvas;
  data: NewWoundData;
  @Input() readOnly;

  canvas;
  points = [];
  bgImg = null;
  heightRatio = 1;
  widthRatio = 1;
  pointRadius = 18;
  subscriptions: Array<any> = [];
  drawDisabled: boolean = false;
  incomingDataLength: number;

  constructor(
    private newWoundService: NewWoundService,
    private alertCtrl: AlertController,
    private translatePipe: TranslateService,
    private platform: Platform
  ) {}

  ngAfterViewInit() {
    // console.log( "View initialized!: ", this.canvasElement );

    let canvas = (this.canvas =
      this.canvasElement.nativeElement.getContext("2d"));
    let width = this.width;
    let height = this.height;

    this.heightRatio = height / this.baseHeight;
    this.widthRatio = width / this.baseWidth;

    canvas.font = "bold 18px Open Sans";
    canvas.textAlign = "center";
    canvas.textBaseline = "middle";

    // console.log( "Height ratio: ", this.heightRatio );
    // console.log( "Width ratio: ", this.widthRatio );

    this.subscriptions.push(
      this.canvasService.getBackgroundObservable().subscribe((url) => {
        // console.log( "Updating background: ", url );
        if (this.bgImg) {
          // console.log( "Old src: ", this.bgImg.src );
        }
        // canvas.clearRect(0, 0, width, height);

        if (!(this.bgImg && url == this.bgImg.src)) {
          this.bgImg = new Image();
          this.bgImg.onload = () => {
            this.drawBG();
            this.drawPoints();
          };
          this.bgImg.src = url;
        }
      })
    );

    this.subscriptions.push(
      this.canvasService.getAllPointDataObservable().subscribe((data) => {
        this.drawDisabled = false;
        //find active point
        for (let i = 0; i < data.points.length; i++) {
          let point = data.points[i];
          if (point["severity"] === 0 && point["active"]) {
            this.drawDisabled = true;
          }
        }

        this.drawBG();
      })
    );
    this.subscriptions.push(
      this.canvasService.getDrawPointsDataObservable().subscribe((data) => {
        if (!data || !data.points) {
          return;
        }

        this.points = data.points;
        // console.log(this.points);

        canvas.clearRect(0, 0, width, height);
        this.drawBG();
        this.drawPoints();
      })
    );

    this.subscriptions.push(
      this.newWoundService.getCurrentPointData().subscribe((currentWound) => {
        if (currentWound) {
          if (currentWound.canvasData) {
            this.newWoundCanvas = currentWound.canvasData;
          }
          this.data = currentWound;
        }
      })
    );
  }

  @Input() set cardData(incomingData: any) {
    if (incomingData && incomingData.length > 0) {
      for (let index = 0; index < incomingData.length; index++) {
        let woundData = incomingData[index];
        if (woundData.recordStatus === "open") {
          this.data = woundData;
          this.onDataUpdate.emit(this.data); // to notify sidebar point still open
          break;
        }
      }
    }
  }

  drawBG() {
    // console.log("drawBG");
    let bg = this.bgImg;
    let canvasElement = this.canvasElement.nativeElement;
    if (bg) {
      // console.log( "Drawing BG: ", bg );

      let imgWidth = bg.width;
      let imgHeight = bg.height;
      let canvasWidth = canvasElement.width - 50;
      let canvasHeight = canvasElement.height - 30;

      let imageAspectRatio = imgWidth / imgHeight;
      let canvasAspectRatio = canvasWidth / canvasHeight;

      let renderableHeight = canvasHeight;
      let renderableWidth = canvasWidth;
      let x = 0;
      let y = 0;

      if (imageAspectRatio < canvasAspectRatio) {
        renderableHeight = canvasHeight;
        renderableWidth = imgWidth * (renderableHeight / imgHeight);
        x = (canvasWidth - renderableWidth) / 2;
      } else if (imageAspectRatio > canvasAspectRatio) {
        renderableWidth = canvasWidth;
        renderableHeight = imgHeight * (renderableWidth / imgWidth);
        y = (canvasHeight - renderableHeight) / 2;
      }

      this.canvas.clearRect(0, 0, this.width, this.height);

      this.canvas.drawImage(
        this.bgImg,
        x + 30,
        y + 20,
        renderableWidth,
        renderableHeight - 30
      );

      if (this.drawDisabled) {
        this.canvas.fillStyle = "rgba(155, 155, 155, 0.5)";
        this.canvas.fillRect(0, 0, this.width, this.height + 30);
      }
    }
  }

  drawPoints() {
    let canvas = this.canvas;

    for (let i = 0; i < this.points.length; i++) {
      let data = this.points[i];

      if (!data["unspecifiedLocation"]) {
        if (data && data["woundStatus"]) {
          if (data["woundStatus"] === "reassess") {
            canvas.fillStyle = "rgb(160, 17, 84, 0.5)";
          } else if (data["woundStatus"] === "healed") {
            canvas.fillStyle = "rgb(133, 191, 49, 0.8)";
          } else if (data["woundStatus"] === "active") {
            canvas.fillStyle = "rgb(74, 144, 226, 0.8)";
          } else {
            // inprogress status
            canvas.fillStyle = "rgba(174, 36, 101, 0.8)";
          }
        } else {
          // defalut status
          canvas.fillStyle = "rgba(174, 36, 101, 0.8)";
        }

        canvas.strokeStyle = "rgb(74, 74, 74)";
        canvas.lineWidth = 0.5;

        let x = this.widthRatio * data["x"];
        let y = this.heightRatio * data["y"];

        canvas.beginPath();
        canvas.arc(x, y, this.pointRadius, 0, 2 * Math.PI);
        canvas.fill();
        canvas.stroke();

        canvas.fillStyle = "white";

        if (data) {
          canvas.fillText(data["locationIndex"] + 1, x, y);
        }
      }
    }
  }

  canvasTouch(event) {
    let rawX = event.srcEvent.offsetX || event.srcEvent.layerX;
    let rawY = event.srcEvent.offsetY || event.srcEvent.layerY;

    // console.log( "EVENT: ", event );
    let x = rawX * (1 / this.widthRatio);
    let y = rawY * (1 / this.heightRatio);

    // console.log( "Clicked at: ", x, y );

    let clickedPoint = this.detectSelection(rawX, rawY);
    // console.log( "Clicked point: ", clickedPoint );

    if (clickedPoint) {
      // here logic for click on existing point
      if (!this.readOnly) {
        if (this.data.woundStatus === "reassess") {
          this.looseDataPopUp("looseDataMessage", clickedPoint);
        } else if (clickedPoint.woundStatus !== "inprogress") {
          this.reassessWoundPopUp("reassessWound", clickedPoint);
        }
      }
    } else {
      if (this.pointsMovable) {
        const dataSet = this.canvasService["dataSet"];
        if (dataSet.length > 0) {
          if (this.data && this.data.recordStatus === "open") {
            // Find array index by locationIndex (they may differ due to default point)
            const arrayIndex = dataSet.findIndex(
              (item) => item && item.locationIndex === this.data.locationIndex
            );
            if (arrayIndex !== -1) {
              this.canvasService["activeIndex"] = arrayIndex;
              this.canvasService.updateActiveCoords(x, y);
              let newWoundCanvas = {
                x: dataSet[arrayIndex].x,
                y: dataSet[arrayIndex].y,
                status: this.data.canvasData.status,
              };
              this.data.canvasData = newWoundCanvas;
              this.onDataUpdate.emit(this.data);
            }
          }
        }
      }
    }
  }

  detectSelection(x: number, y: number) {
    // console.log( "Looking for selection @", x, y );
    // console.log( "Radius: ", this.pointRadius );

    let radius = this.pointRadius;
    for (let i = 0; i < this.points.length; i++) {
      let point = this.points[i];
      // console.log( "Checking point: ", point );

      let rawX = point["x"] * this.widthRatio;
      let rawY = point["y"] * this.heightRatio;

      let dx = x - rawX;
      let dy = y - rawY;
      let collision = dx * dx + dy * dy <= radius * radius;

      // console.log( "DATA: ", dx, dy, collision );

      if (collision) {
        return point;
      }
    }
  }

  reassessWoundPopUp(msg: string, clickedPoint) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant(msg),
      buttons: [
        {
          text: this.translatePipe.instant("cancel"),
          cssClass: this.platform.is("ios")
            ? "alert-greenbtn"
            : "alert-paddingright100 alert-greenbtn text-captilize",
        },
        {
          text: this.translatePipe.instant("proceed"),
          cssClass: "text-captilize",
          handler: () => {
            const index = this.canvasService["dataSet"].length - 1;
            let woundClicked = this.newWoundService.getPointDataByIndex(index);
            if (
              !woundClicked ||
              !woundClicked.woundStatus ||
              woundClicked.woundStatus === "inprogress"
            ) {
              this.canvasService.removePoint(index);
              if (woundClicked) {
                this.newWoundService.removeLastPoint();
              }
            }
            woundClicked = this.newWoundService.getPointDataByIndex(
              clickedPoint.locationIndex
            );
            if (!woundClicked) {
              return;
            }
            const prevWound: PreviousWound = {
              index: clickedPoint.locationIndex,
              status: woundClicked.woundStatus,
            };
            this.newWoundService.setPreviousWoundStatus(prevWound);
            woundClicked.woundStatus = "reassess";
            woundClicked.isReassessed = true;
            this.clearUnrequiredFields(woundClicked);
            this.newWoundService.updateOrPushCurrentPoint(woundClicked);
            this.onDataUpdate.emit(woundClicked);
          },
        },
      ],
    });
    alert.present();
  }

  looseDataPopUp(msg: string, clickedPoint) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant(msg),
      buttons: [
        {
          text: "Cancel",
          cssClass: "text-captilize",
        },
        {
          text: "Continue",
          cssClass: "text-captilize",
          handler: () => {
            let prevWound = this.newWoundService.getPreviousWoundStatus();
            if (prevWound) {
              // update perv data lose status
              let woundClicked = this.newWoundService.getPointDataByIndex(
                prevWound.index
              );
              if (woundClicked) {
                woundClicked.woundStatus = prevWound.status;
                this.newWoundService.updateOrPushCurrentPoint(woundClicked);
              }
            }
            let woundClicked = this.newWoundService.getPointDataByIndex(
              clickedPoint.locationIndex
            );
            if (!woundClicked) {
              return;
            }
            const newPrevWound: PreviousWound = {
              index: clickedPoint.locationIndex,
              status: woundClicked.woundStatus,
            };
            this.newWoundService.setPreviousWoundStatus(newPrevWound);
            woundClicked.woundStatus = "reassess";
            this.clearUnrequiredFields(woundClicked);
            this.newWoundService.updateOrPushCurrentPoint(woundClicked);
            this.onDataUpdate.emit(woundClicked);
          },
        },
      ],
    });
    alert.present();
  }

  clearUnrequiredFields(woundClicked: NewWoundData) {
    woundClicked.stepOneSidebarData.width = "";
    woundClicked.stepOneSidebarData.length = "";
    woundClicked.stepOneSidebarData.depth = "";
    woundClicked.stepOneSidebarData.tracking = false;
    woundClicked.stepOneSidebarData.trackingOther = "";
    woundClicked.stepOneSidebarData.undermining = false;
    woundClicked.stepOneSidebarData.underminingOther = "";
    woundClicked.stepOneSidebarData.stage = "";
    woundClicked.stepOneSidebarData.exudateLevel = "";
    woundClicked.stepOneSidebarData.exudateLevelOther = "";
    woundClicked.stepOneSidebarData.consistency = "";
    woundClicked.stepOneSidebarData.consistencyOther = "";
    woundClicked.stepOneSidebarData.drainageType = "";
    woundClicked.stepOneSidebarData.drainageTypeOther = "";
    woundClicked.stepOneSidebarData.unableToMeasure = false;
    woundClicked.stepOneSidebarData.toolName = "";
    woundClicked.stepOneSidebarData.severity = null;
    woundClicked.notes.additionalNotes = "";
    woundClicked.notes.woundCareTreatment = "";
    woundClicked.score.tool = "";
    woundClicked.score.value = null;
  }

  ngOnDestroy() {
    for (let sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.subscriptions = [];
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/new-wound-site-card/new-wound-canvas/new-wound-canvas.ts